import { accountAccessShowLogin } from 'actions/UiActions'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'
import LinePricetagAltIcon from 'LuxKit/Icons/line/LinePricetagAltIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import { EmptyObject } from 'lib/object/objectUtils'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import MapViewBedbankPricing from '../MapViewOfferTile/MapViewBedbankPricing'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import SearchMapCardLayout from './SearchMapCardLayout'

const StyledGroup = styled(Group)`
  width: 100%;
  padding: ${rem(12)};
`

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  onClose?: () => void
  checkInDate?: moment.Moment
  checkOutDate?: moment.Moment
  windowSearch: string
  isSpoofed: boolean
}

function BedbankSearchMapCard(props: Props) {
  const {
    filters = EmptyObject,
    offer,
    onClose,
    windowSearch,
    isSpoofed,
    checkInDate,
    checkOutDate,
  }: Props = props
  const dispatch = useAppDispatch()
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { name, property, images } = offer

  const { onBedbankSelectDatesPrompt, onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const loginHandler = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  const onSelectDatesPrompt = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    onBedbankSelectDatesPrompt?.(offer.id, offer.type, windowSearch)
  }, [onBedbankSelectDatesPrompt, offer.id, offer.type, windowSearch])

  const handleSelectDatesClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const [{ hotelOnlyRates }] = useBedbankRates(offer.id, filters?.rooms, checkInDate?.format(ISO_DATE_FORMAT), checkOutDate?.format(ISO_DATE_FORMAT))
  const selectedHotelRate = hotelOnlyRates[0]
  const hasMobilePromotion = !!selectedHotelRate?.mobilePromotion
  const {
    nightsToDisplay,
    offerPrice,
  } = useBedbankOfferSearchPricing(offer, filters)

  const hidePrice = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing, offer.productType, filters)
  const hasDisplayablePrice = isDisplayablePrice(offerPrice)

  const intl = useIntl()

  const membersExclusiveRatesText = intl.formatMessage({
    id: 'F8JEQV8tcd8esUiyYErP',
    defaultMessage: 'Members get exclusive rates',
    description: 'Walled content message encouraging members to view exclusive rates',
  })

  const signUpForFreeText = intl.formatMessage({
    id: 'JZZkB6iTlHcgbjW3OZm-',
    defaultMessage: 'Sign up for free',
    description: 'Button text for sign-up call-to-action on map card',
  })

  const selectDatesToViewRatesText = intl.formatMessage({
    id: 'ApSXKk0hX-J0YdPtEiJU',
    defaultMessage: 'Select dates to view rates',
    description: 'Button text prompting user to select dates to see bedbank hotel rates',
  })

  const viewOfferText = intl.formatMessage({
    id: 'g2xmD1R2ECRvmhSLV_yx',
    defaultMessage: 'View offer',
    description: 'Button text to view offer details on mobile map card',
  })

  const selectDatesText = intl.formatMessage({
    id: '0cI9WcelLhiPKVrBtRAQ',
    defaultMessage: 'Select dates',
    description: 'Button text shown when prices are hidden, prompting user to select dates',
  })

  return (
    <SearchMapCardLayout
      images={images}
      onClose={onClose}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind="primary" iconVariant="ghost" />}
    >
      <StyledGroup
        direction="vertical"
        fullWidth
        verticalAlign="space-between"
        gap={hidePrice ? 16 : 4}
      >
        <Group direction="vertical" fullWidth gap={4}>
          <Group direction="horizontal" verticalAlign="start" gap={8}>
            <Group direction="vertical" fullWidth horizontalAlign="space-between">
              {hasMobilePromotion && <BedbankMobileOnlyPriceLabel />}
              <Heading wrap="pre-wrap" variant="heading6">{name}</Heading>
              {isSpoofed && <ProductTypeLabel kind="plain" productType={offer.productType} />}
            </Group>
            {isOfferRatingDisplayable(property.rating) &&
              <OfferRating
                variant="xs"
                rating={property.rating}
                onlyNumber
              />}
          </Group>
        </Group>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={
            <>
              <CaptionTextBlock
                variant="medium"
                weight="bold"
                startIcon={<LinePricetagAltIcon />}
              >
                {membersExclusiveRatesText}
              </CaptionTextBlock>
              <TextButton
                kind="secondary"
                onClick={loginHandler}
                size="small"
                fit="full-width"
              >
                {signUpForFreeText}
              </TextButton>
            </>
          }
        >
          <div>
            {!hasDisplayablePrice && (
              <TextButton kind="secondary" fit="full-width" onClick={onSelectDatesPrompt}>
                {selectDatesToViewRatesText}
              </TextButton>
            )}
            {hasDisplayablePrice && !hidePrice && (
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                <MapViewBedbankPricing
                  filters={filters ?? EmptyObject}
                  offer={offer}
                  price={offerPrice}
                  duration={nightsToDisplay}
                />
                <CSSBreakpoint max="mobile">
                  <TextButton kind="primary">
                    {viewOfferText}
                  </TextButton>
                </CSSBreakpoint>
              </Group>
            )}
            {hasDisplayablePrice && hidePrice && (
              <TextButton kind="primary" fit="full-width" onClick={handleSelectDatesClick}>
                {selectDatesText}
              </TextButton>
            )}
          </div>
        </WalledContent>
      </StyledGroup>
    </SearchMapCardLayout>
  )
}

type MappedStateProps = Pick<Props, 'windowSearch' | 'isSpoofed'>

export default connect<MappedStateProps, undefined, Omit<Props, 'windowSearch' | 'isSpoofed'>, App.State>(
  (state) => ({
    windowSearch: state.router.location.search,
    isSpoofed: state.auth.account.isSpoofed,
  }),
)(BedbankSearchMapCard)
